import { Trash, X } from "lucide-react";
import {
  useDelete,
  useDeleteMany,
  useListContext,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";

import type { Company } from "../types";

const destructiveClassName =
  "cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20";

/**
 * Delete a company after a confirmation that spells out the cascade: the
 * contacts, sponsorings and notes of the company go with it.
 */
export const CompanyDeleteButton = ({
  size = "default",
  label = "resources.companies.action.delete",
  iconOnly = false,
}: {
  size?: "default" | "sm";
  label?: string;
  /** Renders a round icon button (the mobile page header) instead of a labelled one. */
  iconOnly?: boolean;
}) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isOpen, setIsOpen] = useState(false);
  const [deleteOne, { isPending }] = useDelete();

  if (!record) return null;

  const handleConfirm = () => {
    deleteOne(
      "companies",
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          setIsOpen(false);
          notify("resources.companies.deleted", {
            type: "info",
            messageArgs: { smart_count: 1 },
          });
          redirect("list", "companies");
        },
        onError: () => {
          setIsOpen(false);
          notify("resources.companies.delete_error", { type: "error" });
        },
      },
    );
  };

  return (
    <>
      {iconOnly ? (
        <Button
          variant="ghost"
          type="button"
          size="icon"
          className="rounded-full text-destructive"
          aria-label={translate(label)}
          onClick={() => setIsOpen(true)}
        >
          <Trash className="size-5" />
        </Button>
      ) : (
        <Button
          variant="outline"
          type="button"
          size={size}
          className={destructiveClassName}
          onClick={() => setIsOpen(true)}
        >
          <Trash />
          {translate(label)}
        </Button>
      )}
      <Confirm
        isOpen={isOpen}
        loading={isPending}
        title="resources.companies.delete_confirm.title"
        content="resources.companies.delete_confirm.content"
        titleTranslateOptions={{ name: record.name }}
        confirm="resources.companies.action.delete"
        confirmColor="warning"
        ConfirmIcon={Trash}
        CancelIcon={X}
        onConfirm={handleConfirm}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

/**
 * Bulk delete for the companies table, with the same cascade warning.
 */
export const CompanyBulkDeleteButton = () => {
  const { selectedIds, onUnselectItems } = useListContext<Company>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isOpen, setIsOpen] = useState(false);
  const [deleteMany, { isPending }] = useDeleteMany();

  const handleConfirm = () => {
    deleteMany(
      "companies",
      { ids: selectedIds },
      {
        onSuccess: () => {
          setIsOpen(false);
          notify("resources.companies.deleted", {
            type: "info",
            messageArgs: { smart_count: selectedIds.length },
          });
          onUnselectItems();
          refresh();
        },
        onError: () => {
          setIsOpen(false);
          notify("resources.companies.delete_error", { type: "error" });
        },
      },
    );
  };

  return (
    <>
      <Button
        variant="destructive"
        type="button"
        className="h-9"
        onClick={() => setIsOpen(true)}
      >
        <Trash />
        {translate("ra.action.delete")}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={isPending}
        title="resources.companies.bulk_delete_confirm.title"
        content="resources.companies.bulk_delete_confirm.content"
        titleTranslateOptions={{ smart_count: selectedIds.length }}
        confirm="ra.action.delete"
        confirmColor="warning"
        ConfirmIcon={Trash}
        CancelIcon={X}
        onConfirm={handleConfirm}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
